/**
 * Webtech HR Engine™ - SuperAdmin Billing Dashboard
 * Dashboard de gestion de la facturation pour SuperAdmin
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  CreditCard,
  Users,
  DollarSign,
  TrendingUp,
  Building2,
  MoreVertical,
  Play,
  Pause,
  RefreshCw,
  Clock,
  Tag,
  Plus,
  Settings,
  FileText,
  Search,
  Filter,
  Download,
  Eye,
  Crown,
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Percent,
  Calendar
} from 'lucide-react';

export default function SuperAdminBillingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [payments, setPayments] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [paytechSettings, setPaytechSettings] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Promo form
  const [promoForm, setPromoForm] = useState({
    code: '',
    type: 'percentage',
    value: 10,
    scope: 'first_payment',
    validFrom: '',
    validUntil: '',
    maxUses: '',
    description: ''
  });
  
  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    paytechMerchantId: '',
    paytechApiKey: '',
    paytechWebhookSecret: '',
    paytechMode: 'sandbox'
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === 'subscriptions') fetchSubscriptions();
    if (activeTab === 'promo-codes') fetchPromoCodes();
    if (activeTab === 'payments') fetchPayments();
    if (activeTab === 'audit') fetchAuditLogs();
    if (activeTab === 'settings') fetchPaytechSettings();
  }, [activeTab]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await api.get('/superadmin/billing/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await api.get('/superadmin/billing/subscriptions', { params });
      setSubscriptions(response.data.subscriptions);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  };

  const fetchPromoCodes = async () => {
    try {
      const response = await api.get('/superadmin/billing/promo-codes');
      setPromoCodes(response.data.promoCodes);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await api.get('/superadmin/billing/payments');
      setPayments(response.data.payments);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await api.get('/superadmin/billing/audit-logs');
      setAuditLogs(response.data.logs);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  };

  const fetchPaytechSettings = async () => {
    try {
      const response = await api.get('/superadmin/billing/settings/paytech');
      setPaytechSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSubscriptionAction = async (action, additionalData = {}) => {
    if (!selectedSubscription) return;
    
    setActionLoading(true);
    try {
      await api.post(`/superadmin/billing/subscriptions/${selectedSubscription.companyId}/action`, {
        action,
        reason: additionalData.reason || '',
        additionalDays: additionalData.additionalDays,
        discountPercent: additionalData.discountPercent
      });
      
      toast.success('Action effectuée avec succès');
      setShowActionDialog(false);
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'action');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreatePromoCode = async () => {
    try {
      // Validate
      if (!promoForm.code || !promoForm.validFrom || !promoForm.validUntil) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
      
      await api.post('/superadmin/billing/promo-codes', {
        ...promoForm,
        value: parseInt(promoForm.value),
        maxUses: promoForm.maxUses ? parseInt(promoForm.maxUses) : null,
        validFrom: new Date(promoForm.validFrom).toISOString(),
        validUntil: new Date(promoForm.validUntil).toISOString()
      });
      
      toast.success('Code promo créé');
      setShowPromoDialog(false);
      setPromoForm({
        code: '',
        type: 'percentage',
        value: 10,
        scope: 'first_payment',
        validFrom: '',
        validUntil: '',
        maxUses: '',
        description: ''
      });
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await api.put('/superadmin/billing/settings/paytech', settingsForm);
      toast.success('Paramètres mis à jour');
      setShowSettingsDialog(false);
      fetchPaytechSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { label: 'Actif', class: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      suspended: { label: 'Suspendu', class: 'bg-red-100 text-red-700', icon: XCircle },
      grace_period: { label: 'Grâce', class: 'bg-amber-100 text-amber-700', icon: Clock },
      pending: { label: 'En attente', class: 'bg-blue-100 text-blue-700', icon: Clock },
      free: { label: 'Gratuit', class: 'bg-slate-100 text-slate-700', icon: Shield },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={c.class}>
        <Icon className="w-3 h-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title="Facturation">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Gestion de la facturation">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <CreditCard className="w-7 h-7 text-webtech-blue" />
              Facturation SaaS
            </h1>
            <p className="text-slate-500">Gestion des abonnements et paiements</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowSettingsDialog(true)}>
              <Settings className="w-4 h-4 mr-2" />
              PayTech
            </Button>
            <Button onClick={() => setShowPromoDialog(true)} className="bg-webtech-blue hover:bg-webtech-blue-hover">
              <Plus className="w-4 h-4 mr-2" />
              Code Promo
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">MRR</p>
                    <p className="text-xl font-bold">{formatPrice(dashboard.revenue.mrr)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Abonnés PRO</p>
                    <p className="text-xl font-bold">{dashboard.subscriptions.pro}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Revenus ce mois</p>
                    <p className="text-xl font-bold">{formatPrice(dashboard.revenue.monthly)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Suspendus</p>
                    <p className="text-xl font-bold">{dashboard.subscriptions.suspended}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="subscriptions">Abonnements</TabsTrigger>
            <TabsTrigger value="payments">Paiements</TabsTrigger>
            <TabsTrigger value="promo-codes">Codes Promo</TabsTrigger>
            <TabsTrigger value="audit">Audit</TabsTrigger>
            <TabsTrigger value="settings">Paramètres</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="mt-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Répartition des plans</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Crown className="w-5 h-5 text-amber-500" />
                        <span>BUSINESS</span>
                      </div>
                      <span className="font-bold">{dashboard?.subscriptions.pro || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-slate-500" />
                        <span>Gratuit</span>
                      </div>
                      <span className="font-bold">{dashboard?.subscriptions.free || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-amber-500" />
                        <span>Période de grâce</span>
                      </div>
                      <span className="font-bold">{dashboard?.subscriptions.gracePeriod || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Revenus</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Ce mois</span>
                      <span className="font-bold text-green-600">
                        {formatPrice(dashboard?.revenue.monthly || 0)} FCFA
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Total</span>
                      <span className="font-bold">
                        {formatPrice(dashboard?.revenue.total || 0)} FCFA
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">MRR (Revenus récurrents)</span>
                      <span className="font-bold text-blue-600">
                        {formatPrice(dashboard?.revenue.mrr || 0)} FCFA
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Tous les abonnements</CardTitle>
                  <div className="flex gap-2">
                    <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); fetchSubscriptions(); }}>
                      <SelectTrigger className="w-[150px]">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="active">Actifs</SelectItem>
                        <SelectItem value="suspended">Suspendus</SelectItem>
                        <SelectItem value="grace_period">Période grâce</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Entreprise</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Utilisateurs</TableHead>
                      <TableHead>Montant</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Prochain paiement</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptions.map((sub) => (
                      <TableRow key={sub.id}>
                        <TableCell className="font-medium">{sub.companyName}</TableCell>
                        <TableCell>
                          {sub.plan === 'pro' ? (
                            <Badge className="bg-amber-100 text-amber-700">
                              <Crown className="w-3 h-3 mr-1" />PRO
                            </Badge>
                          ) : (
                            <Badge variant="outline">STARTER</Badge>
                          )}
                        </TableCell>
                        <TableCell>{sub.userCount || sub.maxUsers || '-'}</TableCell>
                        <TableCell>{sub.totalAmount ? `${formatPrice(sub.totalAmount)} FCFA` : '-'}</TableCell>
                        <TableCell>{getStatusBadge(sub.status)}</TableCell>
                        <TableCell>{formatDate(sub.nextBillingDate)}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                setSelectedSubscription(sub);
                                setActionType('view');
                              }}>
                                <Eye className="w-4 h-4 mr-2" />Détails
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {sub.status === 'active' && (
                                <DropdownMenuItem onClick={() => {
                                  setSelectedSubscription(sub);
                                  setActionType('suspend');
                                  setShowActionDialog(true);
                                }} className="text-red-600">
                                  <Pause className="w-4 h-4 mr-2" />Suspendre
                                </DropdownMenuItem>
                              )}
                              {sub.status === 'suspended' && (
                                <DropdownMenuItem onClick={() => {
                                  setSelectedSubscription(sub);
                                  setActionType('reactivate');
                                  setShowActionDialog(true);
                                }} className="text-green-600">
                                  <Play className="w-4 h-4 mr-2" />Réactiver
                                </DropdownMenuItem>
                              )}
                              {sub.status === 'grace_period' && (
                                <DropdownMenuItem onClick={() => {
                                  setSelectedSubscription(sub);
                                  setActionType('extend_grace');
                                  setShowActionDialog(true);
                                }}>
                                  <Clock className="w-4 h-4 mr-2" />Prolonger grâce
                                </DropdownMenuItem>
                              )}
                              {sub.plan === 'free' && (
                                <DropdownMenuItem onClick={() => {
                                  setSelectedSubscription(sub);
                                  setActionType('force_activate');
                                  setShowActionDialog(true);
                                }} className="text-amber-600">
                                  <Crown className="w-4 h-4 mr-2" />Activer PRO
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promo-codes" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Codes promotionnels</CardTitle>
                  <Button onClick={() => setShowPromoDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />Nouveau code
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Valeur</TableHead>
                      <TableHead>Portée</TableHead>
                      <TableHead>Utilisations</TableHead>
                      <TableHead>Validité</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {promoCodes.map((promo) => (
                      <TableRow key={promo.id}>
                        <TableCell className="font-mono font-bold">{promo.code}</TableCell>
                        <TableCell>
                          {promo.type === 'percentage' ? (
                            <Badge variant="outline"><Percent className="w-3 h-3 mr-1" />Pourcentage</Badge>
                          ) : (
                            <Badge variant="outline">Fixe</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {promo.type === 'percentage' ? `${promo.value}%` : `${formatPrice(promo.value)} FCFA`}
                        </TableCell>
                        <TableCell>
                          {promo.scope === 'first_payment' ? '1er paiement' : 'Tous'}
                        </TableCell>
                        <TableCell>
                          {promo.currentUses}/{promo.maxUses || '∞'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(promo.validFrom)} - {formatDate(promo.validUntil)}
                        </TableCell>
                        <TableCell>
                          {promo.isActive ? (
                            <Badge className="bg-green-100 text-green-700">Actif</Badge>
                          ) : (
                            <Badge variant="outline">Inactif</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Historique des paiements</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Entreprise</TableHead>
                      <TableHead>Montant</TableHead>
                      <TableHead>Méthode</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Référence</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>{formatDate(payment.createdAt)}</TableCell>
                        <TableCell>{payment.companyName}</TableCell>
                        <TableCell className="font-medium">{formatPrice(payment.amount)} FCFA</TableCell>
                        <TableCell>{payment.paymentMethod || '-'}</TableCell>
                        <TableCell>{getStatusBadge(payment.status)}</TableCell>
                        <TableCell className="font-mono text-xs">{payment.paytechReference || payment.id.slice(0, 8)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Tab */}
          <TabsContent value="audit" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Logs d&apos;audit</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead>Par</TableHead>
                      <TableHead>Détails</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm">{formatDate(log.createdAt)}</TableCell>
                        <TableCell className="font-medium">{log.action}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.category}</Badge>
                        </TableCell>
                        <TableCell>{log.performedByRole}</TableCell>
                        <TableCell className="text-sm text-slate-500 max-w-xs truncate">
                          {JSON.stringify(log.newValue)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Configuration PayTech
                </CardTitle>
                <CardDescription>
                  Paramètres de l&apos;intégration avec PayTech Sénégal. 
                  <a href="https://docs.intech.sn/doc_paytech.php" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                    Documentation officielle
                  </a>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {paytechSettings && (
                  <>
                    {/* Info box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        <strong>Comment obtenir vos clés API ?</strong><br/>
                        1. Connectez-vous sur <a href="https://paytech.sn" target="_blank" rel="noopener noreferrer" className="underline">paytech.sn</a><br/>
                        2. Allez dans <strong>Paramètres → API</strong><br/>
                        3. Copiez votre <strong>API_KEY</strong> et <strong>API_SECRET</strong>
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 border rounded-lg">
                        <Label className="text-slate-500">Mode d&apos;environnement</Label>
                        <p className="font-medium mt-1">
                          <Badge className={paytechSettings.paytechMode === 'production' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                            {paytechSettings.paytechMode === 'production' ? '🟢 Production (Live)' : '🟡 Sandbox (Test)'}
                          </Badge>
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {paytechSettings.paytechMode === 'production' 
                            ? 'Les paiements sont réels' 
                            : 'Mode test - Montant débité: 100-150 FCFA'}
                        </p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <Label className="text-slate-500">API_KEY</Label>
                        <p className="font-medium mt-1">
                          {paytechSettings.paytechApiKeySet ? (
                            <span className="text-green-600">✓ Configurée</span>
                          ) : (
                            <span className="text-red-500">✗ Non configurée</span>
                          )}
                        </p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <Label className="text-slate-500">API_SECRET</Label>
                        <p className="font-medium mt-1">
                          {paytechSettings.paytechMerchantIdSet ? (
                            <span className="text-green-600">✓ Configuré</span>
                          ) : (
                            <span className="text-red-500">✗ Non configuré</span>
                          )}
                        </p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <Label className="text-slate-500">Webhook IPN (optionnel)</Label>
                        <p className="font-medium mt-1">
                          {paytechSettings.paytechWebhookSecretSet ? (
                            <span className="text-green-600">✓ Configuré</span>
                          ) : (
                            <span className="text-slate-400">— Non configuré</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Status global */}
                    <div className={`p-4 rounded-lg ${
                      paytechSettings.paytechApiKeySet && paytechSettings.paytechMerchantIdSet 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-amber-50 border border-amber-200'
                    }`}>
                      <p className={`font-medium ${
                        paytechSettings.paytechApiKeySet && paytechSettings.paytechMerchantIdSet 
                          ? 'text-green-800' 
                          : 'text-amber-800'
                      }`}>
                        {paytechSettings.paytechApiKeySet && paytechSettings.paytechMerchantIdSet 
                          ? '✓ PayTech est configuré et prêt à recevoir des paiements' 
                          : '⚠ Configuration incomplète - Les paiements fonctionneront en mode simulation'}
                      </p>
                    </div>

                    <Button onClick={() => setShowSettingsDialog(true)} className="bg-webtech-blue hover:bg-webtech-blue-hover">
                      <Settings className="w-4 h-4 mr-2" />
                      Configurer PayTech
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Promo Code Dialog */}
        <Dialog open={showPromoDialog} onOpenChange={setShowPromoDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Créer un code promo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  value={promoForm.code}
                  onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                  placeholder="PROMO2024"
                  className="uppercase"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={promoForm.type} onValueChange={(v) => setPromoForm({ ...promoForm, type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Pourcentage (%)</SelectItem>
                      <SelectItem value="fixed">Montant fixe (FCFA)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Valeur</Label>
                  <Input
                    type="number"
                    value={promoForm.value}
                    onChange={(e) => setPromoForm({ ...promoForm, value: e.target.value })}
                    placeholder={promoForm.type === 'percentage' ? '10' : '5000'}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Portée</Label>
                <Select value={promoForm.scope} onValueChange={(v) => setPromoForm({ ...promoForm, scope: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first_payment">Premier paiement uniquement</SelectItem>
                    <SelectItem value="all_payments">Tous les paiements</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date début *</Label>
                  <Input
                    type="date"
                    value={promoForm.validFrom}
                    onChange={(e) => setPromoForm({ ...promoForm, validFrom: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date fin *</Label>
                  <Input
                    type="date"
                    value={promoForm.validUntil}
                    onChange={(e) => setPromoForm({ ...promoForm, validUntil: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Max utilisations (optionnel)</Label>
                <Input
                  type="number"
                  value={promoForm.maxUses}
                  onChange={(e) => setPromoForm({ ...promoForm, maxUses: e.target.value })}
                  placeholder="Illimité"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPromoDialog(false)}>Annuler</Button>
              <Button onClick={handleCreatePromoCode}>Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Settings Dialog */}
        <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configuration PayTech
              </DialogTitle>
              <DialogDescription>
                Entrez vos clés API PayTech. Vous pouvez les obtenir depuis votre 
                <a href="https://paytech.sn" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                  dashboard PayTech → Paramètres → API
                </a>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Mode */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Mode d&apos;environnement
                  <span className="text-xs text-slate-400">(important)</span>
                </Label>
                <Select value={settingsForm.paytechMode} onValueChange={(v) => setSettingsForm({ ...settingsForm, paytechMode: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">
                      <span className="flex items-center gap-2">
                        🟡 Sandbox (Test) - Pour le développement
                      </span>
                    </SelectItem>
                    <SelectItem value="production">
                      <span className="flex items-center gap-2">
                        🟢 Production - Paiements réels
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {settingsForm.paytechMode === 'sandbox' 
                    ? "En mode test, PayTech ne débitera que 100-150 FCFA pour chaque transaction." 
                    : "⚠️ En production, les paiements seront réels. Assurez-vous que votre compte est activé."}
                </p>
              </div>

              {/* API Key */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  API_KEY
                  <span className="text-xs text-red-500">*</span>
                </Label>
                <Input
                  type="password"
                  value={settingsForm.paytechApiKey}
                  onChange={(e) => setSettingsForm({ ...settingsForm, paytechApiKey: e.target.value })}
                  placeholder="Collez votre API_KEY ici"
                />
                <p className="text-xs text-slate-500">
                  Clé publique PayTech (commence généralement par une longue chaîne)
                </p>
              </div>

              {/* API Secret */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  API_SECRET
                  <span className="text-xs text-red-500">*</span>
                </Label>
                <Input
                  type="password"
                  value={settingsForm.paytechMerchantId}
                  onChange={(e) => setSettingsForm({ ...settingsForm, paytechMerchantId: e.target.value })}
                  placeholder="Collez votre API_SECRET ici"
                />
                <p className="text-xs text-slate-500">
                  Clé secrète PayTech - Ne la partagez jamais
                </p>
              </div>

              {/* Note */}
              <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                <strong>💡 Astuce:</strong> Laissez les champs vides si vous ne souhaitez pas modifier les valeurs existantes.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>Annuler</Button>
              <Button onClick={handleUpdateSettings} className="bg-webtech-blue hover:bg-webtech-blue-hover">
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Action Dialog */}
        <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionType === 'suspend' && 'Suspendre l\'abonnement'}
                {actionType === 'reactivate' && 'Réactiver l\'abonnement'}
                {actionType === 'extend_grace' && 'Prolonger la période de grâce'}
                {actionType === 'force_activate' && 'Activer le plan PRO'}
              </DialogTitle>
              <DialogDescription>
                {selectedSubscription?.companyName}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {actionType === 'extend_grace' && (
                <div className="space-y-2">
                  <Label>Jours supplémentaires</Label>
                  <Input type="number" id="additionalDays" defaultValue={7} />
                </div>
              )}
              <div className="space-y-2">
                <Label>Raison (optionnel)</Label>
                <Input id="actionReason" placeholder="Raison de l'action" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowActionDialog(false)}>Annuler</Button>
              <Button
                onClick={() => {
                  const reason = document.getElementById('actionReason')?.value;
                  const additionalDays = document.getElementById('additionalDays')?.value;
                  handleSubscriptionAction(actionType, { reason, additionalDays: parseInt(additionalDays) });
                }}
                disabled={actionLoading}
                className={actionType === 'suspend' ? 'bg-red-600 hover:bg-red-700' : ''}
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
